import { Radio } from 'components/Forms'
import { LoadingAnimation } from '@outbound-ai/ursa'
import { updateCallDisposition } from 'features/calls/callsApi'
import { CallDispositionEnum, ICall } from 'features/calls/types'
import { IClaim } from 'features/claims/types'
import { useRevalidateAllSwrCache } from 'hooks/useRevalidateAllSwrCache'
import { keys } from 'lodash'
import { useEffect, useState } from 'react'
import { Alert, Button, ObModal } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { ErrorMessagesEnum } from 'types/errorTypes'
import { handleApiRequestWithStates } from 'utils/api'
import { errorHandler } from 'utils/errors/errors'

interface IProps {
  call: ICall
  claim: IClaim
  show: boolean
  setShow: React.Dispatch<React.SetStateAction<boolean>>
}

export const SelectCallDispositionModal = ({ show, setShow, call }: IProps) => {
  // utils
  const revalidateAllSwrCache = useRevalidateAllSwrCache()
  const callDispositions = keys(CallDispositionEnum) as (keyof typeof CallDispositionEnum)[]
  const disposition = call.disposition || 0

  const [checked, setChecked] = useState<keyof typeof CallDispositionEnum>(callDispositions[disposition])

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (callDispositions[disposition] !== checked) setChecked(callDispositions[disposition])
  }, [disposition])

  // handlers
  const handleSaveDisposition = async () => {
    if (callDispositions.indexOf(checked) !== call?.disposition) {
      const { data, error } = await handleApiRequestWithStates(
        () => updateCallDisposition(call.jobId, callDispositions.indexOf(checked)),
        setIsLoading,
      )
      if (data) {
        revalidateAllSwrCache()
        setShow(false)
      }

      if (error) errorHandler.handleError(error, ErrorMessagesEnum.UpdateCallDisposition)
    } else {
      setError(`${checked} is already selected`)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(e.target.value as keyof typeof CallDispositionEnum)
    if (error) setError('')
  }

  // variables
  const callDispositionOptions = callDispositions.map((callDisposition) => {
    return {
      id: callDisposition,
      value: callDisposition,
      label: CallDispositionEnum[callDisposition],
    }
  })

  return (
    <ObModal
      show={show}
      close={() => setShow(false)}
      size="lg"
      title={'Select Call Disposition'}
      height="auto"
      modalFooter={
        <FooterStyle>
          <Button
            variant="secondary"
            radius="rounded"
            size="large"
            width="100%"
            className="take-ownership-btn"
            onClick={handleSaveDisposition}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <LoadingAnimation /> Saving...
              </>
            ) : (
              <> Save Call Disposition</>
            )}
          </Button>

          <Button variant="primary" radius="rounded" size="large" width="100%" onClick={() => setShow(false)}>
            Cancel
          </Button>
        </FooterStyle>
      }
    >
      <div className="px-4 py-3 d-flex flex-column gap-3">
        <Radio
          name="callDisposition"
          options={callDispositionOptions}
          onChange={handleChange}
          checked={checked}
          label="What was the outcome of this call?"
          className="bodyContent"
        />

        {error && (
          <div className="mb-0">
            <Alert
              variant="danger"
              show={true}
              dismissible={false}
              onClickClose={() => {
                // no-op: non-dismissible alert
              }}
            >
              {error}
            </Alert>
          </div>
        )}
      </div>
    </ObModal>
  )
}

const FooterStyle = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  width: -webkit-fill-available;
`
